/**
 * Rate-limit usage hook.
 *
 * Polls /api/leads/credits/usage every 60s and exposes the user's current
 * daily usage vs. their plan caps. Used by the top-bar banner + 80%-threshold
 * toast warnings so users see how close they are to hitting a hard block.
 *
 * The endpoint also reports `any_approaching` (>=80%) and `any_at_limit`
 * (>=100%) rollups so the banner can decide visibility without scanning
 * every endpoint shape on every render.
 *
 * Polling cadence: 60s — fast enough that users see "11 / 15 replies" tick
 * up while they work, slow enough that the bell-dropdown / inbox polling
 * doesn't compound it into a noisy network log. Pause when tab is hidden
 * via React Query's default behavior (refetchOnWindowFocus).
 */

import { useQuery } from '@tanstack/react-query'
import { useApi } from './use-api'

export type RateLimitState = 'ok' | 'approaching_limit' | 'at_limit' | 'blocked_on_plan'

export type RateLimitEndpointUsage = {
  readonly key: string
  readonly label: string
  readonly used: number
  readonly cap: number | null
  readonly remaining: number | null
  readonly percent: number
  readonly burst_per_minute: number
  readonly state: RateLimitState
}

export type RateLimitUsage = {
  readonly plan: string
  readonly endpoints: readonly RateLimitEndpointUsage[]
  readonly resets_at: string
  readonly any_at_limit: boolean
  readonly any_approaching: boolean
}

export const rateLimitsQueryKey = ['rate-limits', 'usage'] as const

export function useRateLimitUsage() {
  const apiFetch = useApi()
  return useQuery({
    queryKey: rateLimitsQueryKey,
    queryFn: async (): Promise<RateLimitUsage | null> => {
      const res = await apiFetch<RateLimitUsage>('/api/leads/credits/usage')
      if (!res.success || !res.data) return null
      return res.data
    },
    refetchInterval: 60_000,
    // staleTime less than refetchInterval so a manual mutation can invalidate
    // and force a re-fetch without waiting for the next tick.
    staleTime: 30_000,
  })
}

/**
 * Format "Resets in 4h 12m" from the ISO `resets_at` timestamp. Pure helper
 * so component code doesn't need to do date math inline.
 */
export function formatResetCountdown(isoTimestamp: string | undefined): string {
  if (!isoTimestamp) return ''
  const reset = Date.parse(isoTimestamp)
  if (Number.isNaN(reset)) return ''
  const ms = reset - Date.now()
  if (ms <= 0) return 'any moment'
  const totalMinutes = Math.floor(ms / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}
